import { filter, map } from 'fp-ts/Array';
import { flow, pipe } from 'fp-ts/function';
import { isEmpty } from 'fp-ts/Record';
import { get, identity, filter as lodashFilter, map as lodashMap, lt, mergeWith, reduce, size, some } from 'lodash/fp';
import { createSelector } from 'reselect';

import { sumMultiCurrencyAmounts } from '@mews-distributor/common';
import { getOrEmptyArray, getStringProp } from '@mews/fp';
import { composeProps } from '@mews/framework/redux';
import { getSelectedCategoryAvailableRateIds, getSelectedOccupancyPricings } from '../../modules/pricing/selectors';
import { PricingRate, PricingValue } from '../../modules/pricing/types';
import { OccupancyData } from '../../modules/resourceOrder/occupancyTypes';
import { getEndDate, getStartDate, selectedEnterpriseIdPropsExtender } from '../../modules/resourceOrder/selectors';
import { getServiceAvailabilitiesInLegacyFormat } from '../../modules/serviceAvailability/selectors';
import { AvailabilityValue } from '../../types/data';
import { getCurrentDateEnterpriseAvailability } from './typedAvailability';

interface OcupancyPricing {
    occupancy:
        | {
              occupancyData: OccupancyData[];
              adultCount?: undefined;
              childCount?: undefined;
          }
        | {
              adultCount: number | undefined;
              childCount: number | undefined;
              occupancyData?: undefined;
          };
    pricing: PricingValue;
}

export const getSelectedIntervalAvailability = createSelector(
    getServiceAvailabilitiesInLegacyFormat,
    getStartDate,
    getEndDate,
    (availability, startDate, endDate) => lodashFilter({ startDate, endDate }, availability)
);
const getAvailabilityByHash = createSelector(
    getStringProp('availabilityHash'),
    getServiceAvailabilitiesInLegacyFormat,
    get
);

export const getAvailabilityPricingHashes = createSelector(
    getAvailabilityByHash,
    flow(get('categories'), lodashMap('pricingHash'))
);

export const getCurrentDateAvailability = composeProps(
    getCurrentDateEnterpriseAvailability,
    selectedEnterpriseIdPropsExtender
);

export const getCurrentDateAvailabilityViolatedRestrictionIds = createSelector(
    // @ts-expect-error $TSFixMeError: Argument of type 'unknown' is not assignable to parameter of type 'ApplicationState'.
    (state) => getCurrentDateAvailability(state, {}),
    getOrEmptyArray('violatedRestrictionIds')
);

export const isAvailabilityFetching = createSelector(
    getCurrentDateAvailability,
    (availability) => availability.isFetching
);

export const hasAvailabilityFailed = createSelector(
    getCurrentDateAvailability,
    (availability) => availability.hasFailed
);

export const getCurrentDateEnterpriseAvailableCategories = flow(
    getCurrentDateEnterpriseAvailability,
    get<AvailabilityValue | Partial<AvailabilityValue>, 'categories'>('categories'),
    lodashFilter((category) => category.availableCount > 0)
);

export const hasCurrentDateEnterpriseAvailableCategories = flow(
    getCurrentDateEnterpriseAvailableCategories,
    size,
    lt(0)
);

export const getCurrentDateAvailableCategories = composeProps(
    getCurrentDateEnterpriseAvailableCategories,
    selectedEnterpriseIdPropsExtender
);

export const isAnyAvailabilityFetching = createSelector(
    getServiceAvailabilitiesInLegacyFormat,
    flow(lodashMap('isFetching'), some(identity))
);

const getPrices = (ratePricing: $TSFixMe) => ({
    pricePerNight: get(['price', 'averageAmountPerTimeUnit'], ratePricing),
    maxPricePerNight: get(['maxPrice', 'averageAmountPerTimeUnit'], ratePricing),
    pricePerFullTimeUnit: get(['price', 'averageAmountPerFullTimeUnit'], ratePricing),
    maxPricePerFullTimeUnit: get(['maxPrice', 'averageAmountPerFullTimeUnit'], ratePricing),
    totalPrice: get(['price', 'totalAmount'], ratePricing),
    cityTaxIncludedInPrice: ratePricing?.cityTaxIncludedInPrice,
});

export const getSelectedRoomAvailableRatePrices = createSelector(
    getSelectedCategoryAvailableRateIds,
    getSelectedOccupancyPricings,
    (rateIds: string[], occupancyPricings: OcupancyPricing[]) =>
        pipe(
            rateIds,
            map((rateId) => mapRateIdToPrice(occupancyPricings, rateId)),
            (prices) => Object.assign({}, ...prices)
        )
);

function mapRateIdToPrice(occupancyPricings: OcupancyPricing[], rateId: string) {
    const price = pipe(
        occupancyPricings,
        filter((occupancyPricing) => occupancyPricing.pricing?.rates?.[rateId] != null),
        map((occupancyPricing) => getPrices(occupancyPricing.pricing.rates[rateId])),
        reduce(
            (sum, ratePricing: Record<string, PricingRate>) => ({
                ...mergeWith(sumMultiCurrencyAmounts, ratePricing, sum),
                cancellationFees: ratePricing?.cancellationFees,
                cityTaxIncludedInPrice: ratePricing.cityTaxIncludedInPrice,
                sellLimitStatistic: ratePricing.sellLimitStatistic,
            }),
            {}
        )
    );
    return isEmpty(price) ? {} : { [rateId]: price };
}
